import { hasAppAudience } from "../../access-control/models/access-groups.models";
import { useAuthContext } from "../../auth/state/AuthContext";
import { canAccessVesselCard } from "../../pbi/vessel-card/utils/vessel-card-access.utils";
import { CaretDownIcon, DotIcon, SpeedometerIcon } from "../../shared/Icons";
import { ReactComponent as CaretRight } from "../../shared/assets/caretRight.svg";
import { themeAtom } from "../../shared/atoms";
import Rating from "../../shared/components/Rating";
import { formatDate } from "../../shared/services/utils";
import { TimeCharter, VesselDetails } from "../models/search.interfaces";
import { parseSortAndFilterTcs } from "../services/search-vessels.service";
import { ExpandedSearchRow } from "./ExpandedSearchRow";
import { VesselsRowMenu } from "./VesselsRowMenu";
import { useAtomValue } from "jotai";
import { Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

const Td = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <td className={twMerge("h-14 px-4 py-2", className)}>{children}</td>
);

export function SearchVesselsTableRow({
  showSelectedOnly,
  fullName,
  handleVesselNameClick,
  timeCharters,
  item,
  index,
  isOpen,
  toggleAccordion,
  setIsLoading,
}: {
  showSelectedOnly: boolean;
  fullName: string;
  handleVesselNameClick: (item: VesselDetails) => void;
  timeCharters: TimeCharter[];
  item: any;
  index: number;
  isOpen: boolean;
  toggleAccordion: (toggleIndex: number) => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}) {
  const theme = useAtomValue(themeAtom);
  const user = useAuthContext().state.user;
  // Keeps direct card links limited to the same testers and role grants as the card page.
  const canViewVesselCard =
    hasAppAudience(user?.accessToken?.groups ?? []) &&
    canAccessVesselCard(user?.accessToken, user?.accessControl) &&
    /^\d{7}$/.test(String(item.IMO_NUMBER ?? ""));

  const cpDate =
    item.CP_DATE === undefined
      ? parseSortAndFilterTcs(item.TCCODE_TCSTATUS_CPDELDT_CPREDELDT_VOYAGENO).cpDate
      : undefined;
  // Default rows retain the legacy selection; advanced rows use the server-selected date.
  const displayedCpDate =
    item.CP_DATE !== undefined
      ? item.CP_DATE
      : ["Delivered", "Fixed", "On Subjects"].includes(cpDate?.tc_status ?? "")
        ? cpDate?.cp_date
        : null;

  const truncateText = (text: string = "") => {
    return text.length > 10 ? `${text.slice(0, 10)}...` : text;
  };

  const getStatusColor = (status: string): { border: string; text: string } => {
    const s = status?.toLowerCase?.() ?? "";
    const statusMap = theme.myVesselsTableRow.status as Record<string, { border: string; text: string }>;

    const picked = statusMap[s] || statusMap[status] || statusMap.default;

    return picked;
  };

  return (
    <>
      <tr
        className={twMerge(
          "cursor-pointer border-b text-left text-xs",
          isOpen && "border-l-2 !border-l-green",
          theme.uwhcAssistant.background,
          theme.myVesselsTableRow.row.hover,
          theme.myVesselsTableRow.tableText.primary,
          theme.myVesselsTable.tailwind.tableBorder,

          showSelectedOnly && item.FROM_IMOS === "N|Y" && item.USER_NAME === fullName && "hidden",
        )}
        key={index}
        onClick={() => toggleAccordion(index)}
      >
        <Td className="flex items-center text-green">
          {isOpen ? <CaretDownIcon className="mr-2" /> : <CaretRight className="mr-2" />}
          <DotIcon
            width={10}
            height={10}
            style={{
              color: item.OPSLINK_ACTIVE ? theme.myVesselsTableRow.dot.active : theme.myVesselsTableRow.dot.inactive,
            }}
          />
          <button className="ml-1" onClick={() => handleVesselNameClick(item)}>
            {item.VESSEL_NAME}
          </button>
        </Td>
        <Td className="h-14 w-48 px-4 py-2">{displayedCpDate ? formatDate(displayedCpDate) : "-"}</Td>
        <Td>
          <p>{item.IMO_NUMBER}</p>
        </Td>
        <Td>
          <p>{item.VESSEL_TYPE}</p>
        </Td>
        <Td>
          <p>{item.OWNER}</p>
        </Td>
        <Td>
          <p>{item.SUMMER_DWT}</p>
        </Td>
        <Td>
          <div>
            <div className="inline-flex h-4 w-6 items-start justify-start gap-0.5">
              <div className={twMerge("text-xs font-semibold", theme.myVesselsTableRow.tableText.primary)}>
                {item.RIGHTSHIP_SAFETY_SCORE}
              </div>
              <div className="text-xs text-neutral-400">/5</div>
            </div>
          </div>
        </Td>
        <Td>{item.RIGHTSHIP_GHG_RATING && <Rating label={item.RIGHTSHIP_GHG_RATING} />}</Td>

        <Td className="relative">
          <Tooltip
            id="search-tooltip"
            disableStyleInjection={true}
            style={{
              color: theme.myVesselsTableRow.tooltip.text,
              background: theme.myVesselsTableRow.tooltip.background,
              borderRadius: "8px",
              position: "absolute",
              right: "0",
            }}
            place="left"
          />
          <div className="flex items-center justify-end gap-3">
            {canViewVesselCard && (
              <Link
                to={`/vessel-card/${item.IMO_NUMBER}`}
                aria-label={`Open Vessel Card for ${item.VESSEL_NAME}`}
                data-test="search-vessels-vessel-card-link"
                data-tooltip-id="search-tooltip"
                data-tooltip-html="Open Vessel Card"
                className="rounded text-green focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green"
                onClick={(event) => event.stopPropagation()}
              >
                <SpeedometerIcon className="h-6 w-6" aria-hidden="true" />
              </Link>
            )}
            <VesselsRowMenu fullName={fullName} item={item} setIsLoading={setIsLoading} />
          </div>
        </Td>
      </tr>

      {isOpen && (
        <ExpandedSearchRow timeCharters={timeCharters} getStatusColor={getStatusColor} truncateText={truncateText} />
      )}
    </>
  );
}
